/**
 * Input event protocol — sent over the `input` DataChannel.
 * Mirrors `host-agent/core/src/proto.rs::InputEvent`.
 *
 * Coordinates are NORMALIZED to the video frame (`x, y ∈ [0, 1]`). The host maps them onto the
 * captured monitor's pixel rectangle, so the same payload format works regardless of remote
 * resolution.
 */

export type MouseButton = 'left' | 'right' | 'middle';

export interface KeyModifiers {
  shift?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  meta?: boolean;
}

export type InputEvent =
  | { type: 'mouse_move'; x: number; y: number }
  | { type: 'mouse_button'; button: MouseButton; pressed: boolean; x: number; y: number }
  | { type: 'wheel'; dx: number; dy: number }
  | { type: 'key_down'; code: string; modifiers: KeyModifiers }
  | { type: 'key_up'; code: string; modifiers: KeyModifiers }
  /**
   * Direct Unicode text injection. Used by the on-screen keyboard for every printable character:
   * the host calls `SendInput` with `KEYEVENTF_UNICODE`, so the literal glyph lands in the focused
   * window regardless of the host's active keyboard layout. Sending `KeyA` via `key_down` produces
   * `ф` on a RU-layout host — `unicode_text` sidesteps that entirely.
   *
   * Special keys (Backspace, Enter, Tab, Arrow*, Escape, etc.) still flow through `key_down`/
   * `key_up` because they're not characters and the host scancode path handles them correctly.
   */
  | { type: 'unicode_text'; text: string };

export function sendInput(channel: RTCDataChannel | null, ev: InputEvent): void {
  if (!channel || channel.readyState !== 'open') return;
  try {
    channel.send(JSON.stringify(ev));
  } catch {
    /* swallow — DC may close mid-send */
  }
}
